import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { BehaviorSubject, Observable, catchError, take } from 'rxjs';
import { ServiceBase } from './servicebase.service';

@Injectable({
  providedIn: 'root'
})
export class PeopleService extends ServiceBase {
  findPeopleResultSubj: BehaviorSubject<ServerModels.ENESS_PersonDTO[]> = new BehaviorSubject<ServerModels.ENESS_PersonDTO[]>([]);
  findPeopleResult$: Observable<ServerModels.ENESS_PersonDTO[]> = this.findPeopleResultSubj.asObservable();

  personLECasesSubj: BehaviorSubject<ServerModels.PersonLECases[]> = new BehaviorSubject([]);
  personLECases$: Observable<ServerModels.PersonLECases[]> = this.personLECasesSubj.asObservable();

  personInfoReportsSubj: BehaviorSubject<{ 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]> = new BehaviorSubject([]);
  personInfoReports$: Observable<{ 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]> = this.personInfoReportsSubj.asObservable();

  personResultsSubj: BehaviorSubject<{ 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]> = new BehaviorSubject([]);
  personResults$: Observable<{ 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]> = this.personResultsSubj.asObservable();

  personTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_PersonTypes[]> = new BehaviorSubject([]);
  personTypes$: Observable<ServerModels.ENESS_REF_PersonTypes[]> = this.personTypesSubj.asObservable();

  personFilterTypesSubj: BehaviorSubject<ServerModels.ENESS_PersonFilters[]> = new BehaviorSubject([]);
  personFilterTypes$: Observable<ServerModels.ENESS_PersonFilters[]> = this.personFilterTypesSubj.asObservable();

  addressTypesSubj: BehaviorSubject<ServerModels.ENESS_REF_AddressTypes[]> = new BehaviorSubject([]);
  addressTypes$: Observable<ServerModels.ENESS_REF_AddressTypes[]> = this.addressTypesSubj.asObservable();

  private _personDetails: ServerModels.ENESS_PersonDTO = null;
  public personDetails: BehaviorSubject<ServerModels.ENESS_PersonDTO> = new BehaviorSubject<ServerModels.ENESS_PersonDTO>(this._personDetails);
  public personDetails$: Observable<ServerModels.ENESS_PersonDTO> = this.personDetails.asObservable();

  constructor(protected http: HttpClient,
    public messageService: MessageService) {
    super(http, messageService);
    this.getAllPeopleTypes();
    this.getPeopleFilters();
    this.getAddressTypes();
  }

  public addOrUpdatePerson(person: ServerModels.ENESS_PersonDTO): Observable<ServerModels.ENESS_PersonDTO> {
    return this.http.post<ServerModels.ENESS_PersonDTO>(`${this.apiRoot}/api/Person/AddOrUpdatePerson`, person, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addOrUpdatePerson', null, null, null)));
  }

  public addOrUpdatePersonStagingAsync(personDto: ServerModels.ENESS_PersonDTO): Observable<ServerModels.ENESS_PersonDTO> {
    return this.http.post<ServerModels.ENESS_PersonDTO>(`${this.apiRoot}/api/Person/AddOrUpdatePersonStagingAsync`, personDto, this.httpOptions)
      .pipe(catchError(this.throwError<any>('addOrUpdatePersonStagingAsync', null, null, null)));
  }

  public deletePersonAttachment(attachmentID: string): Observable<number> {
    return this.http.post<number>(`${this.apiRoot}/api/Person/DeletePersonAttachment`, JSON.stringify(attachmentID), this.httpOptions)
      .pipe(catchError(this.throwError<any>('deletePersonAttachment', null, null, null)));
  }

  public deletePersonAttachmentStaging(personAttachmentStagingId: string): Observable<number> {
    return this.http.post<number>(`${this.apiRoot}/api/Person/DeletePersonAttachmentStaging`, JSON.stringify(personAttachmentStagingId), this.httpOptions)
      .pipe(catchError(this.throwError<any>('deletePersonAttachmentStaging', null, null, null)));
  }

  public findPeople(filterValues: ServerModels.FilterValues[]): Observable<ServerModels.ENESS_PersonDTO[]> {
    return this.http.post<ServerModels.ENESS_PersonDTO[]>(`${this.apiRoot}/api/Person/FindPeople`, filterValues, this.httpOptions)
      .pipe(catchError(this.throwError<any>('findPeople', null, null, null)));
  }

  public getAllPeopleTypes() {
    this.http.get<ServerModels.ENESS_REF_PersonTypes[]>(`${this.apiRoot}/api/Person/AllPeopleTypes`, this.httpOptions)
      .pipe(
        take(1),
       catchError(this.swallowError<any>('getAllPeopleTypes', 'defaultMsg', null, null))
    ).subscribe((results: ServerModels.ENESS_REF_PersonTypes[]) => this.personTypesSubj.next(results))
  }

  public getAttachmentById(attachmentId: string): Observable<Blob> {
    let options = {
      responseType: 'blob' as 'json',
      params: {
        'attachmentId': attachmentId
      }
    };
    return this.http.get<Blob>(`${this.apiRoot}/api/Person/GetAttachmentById`, options)
      .pipe(catchError(this.swallowError<any>('getAttachmentById', 'defaultMsg', null, null))); 
  }

  public getAttachmentByIdStaging(attachmentStagingId: string): Observable<Blob> {
    let options = {
      responseType: 'blob' as 'json',
      params: {
        'attachmentStagingId': attachmentStagingId
      }
    };
    return this.http.get<Blob>(`${this.apiRoot}/api/Person/GetAttachmentByIdStaging`, options)
      .pipe(catchError(this.throwError<any>('getAttachmentByIdStaging', null, null, null)));
  }

  public getPeopleFilters() {
    this.http.get<ServerModels.ENESS_PersonFilters[]>(`${this.apiRoot}/api/Person/GetPeopleFilters`, this.httpOptions)
      .pipe(
        take(1),
        catchError(this.throwError<any>('getPeopleFilters', null, null, null))
    ).subscribe((filters: ServerModels.ENESS_PersonFilters[]) => this.personFilterTypesSubj.next(filters));
  }

  public getPersonDTOById(personId: string): Observable<ServerModels.ENESS_PersonDTO> | null {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personId': personId
      }
    };
    return this.http.get<ServerModels.ENESS_PersonDTO>(`${this.apiRoot}/api/Person/GetPersonDTOById`, options)
      .pipe(catchError(this.swallowError<any>('getPersonDTOById', 'defaultMsg', null, null))); 
  }

  public getPersonDetailsDTOById(personId: string): Observable<ServerModels.ENESS_PersonDTO> | null {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personId': personId
      }
    };
    return this.http.get<ServerModels.ENESS_PersonDTO>(`${this.apiRoot}/api/Person/GetPersonDetailsDTOById`, options)
      .pipe(catchError(this.swallowError<any>('getPersonDetailsDTOById', 'defaultMsg', null, null))); 
  }

  public getPersonStagingById(personStagingId: string): Observable<ServerModels.ENESS_PersonDTO> | null {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personStagingId': personStagingId
      }
    };
    return this.http.get<ServerModels.ENESS_PersonDTO>(`${this.apiRoot}/api/Person/GetPersonStagingById`, options)
      .pipe(catchError(this.throwError<any>('getPersonStagingById', 'defaultMsg', null, null))); 
  }

  public getPersonInfoReports(personId: string): Observable<{ 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      params: {
        'personId': personId
      },
      headers: headersObj
    };

    return this.http.get<{ 'IntelligenceTrackingNumber': string, 'CreatedOn': Date }[]>(`${this.apiRoot}/api/Person/GetPersonInfoReports`, options)
      .pipe(catchError(this.throwError<any>('getPersonInfoReports', null, null, null)));
  }

  public getPersonResults(personId: string): Observable<{ 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      params: {
        'personId': personId
      },
      headers: headersObj
    };

    return this.http.get<{ 'InvestigativeTrackingNumber': string, 'CreatedOn': Date }[]>(`${this.apiRoot}/api/Person/GetPersonResults`, options)
      .pipe(catchError(this.swallowError<any>('getPersonResults', 'defaultMsg', null, null))); 
  }

  public getPersonLECases(personId: string): Observable<ServerModels.PersonLECases[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      params: {
        'personId': personId
      },
      headers: headersObj
    };

    return this.http.get<ServerModels.PersonLECases[]>(`${this.apiRoot}/api/Person/GetPersonLECases`, options)
      .pipe(catchError(this.throwError<any>('getPersonLECases', 'defaultMsg', null, null))); 
  }

  public DeletePerson(data: ServerModels.ENESS_DeleteHistory): Observable<ServerModels.ENESS_Person> {
    return this.http.post<ServerModels.ENESS_Person>(`${this.apiRoot}/api/Person/DeletePerson`, data, this.httpOptions)
       .pipe(catchError(this.swallowError<any>('DeletePerson', 'defaultMsg', null, null))); 
  }

  public hasDeletePermission(personId: string): Observable<boolean> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personId': personId
      }
    };
    return this.http.get<boolean>(`${this.apiRoot}/api/Person/HasDeletePermission`, options)
      .pipe(catchError(this.throwError<any>('hasDeletePermission', 'defaultMsg', null, null)));
  }

  public getPeopleViewModel(filters: ServerModels.FiltersViewModel): Observable<ServerModels.PeopleViewModel[]> {
    let headersObj = this.httpOptions.headers.append('Skip-Loader', 'true');
    let options = {
      headers: headersObj
    };
    return this.http.post<ServerModels.PeopleViewModel[]>(`${this.apiRoot}/api/Person/AllPeopleViewModelPaged`, filters, options)
      .pipe(catchError(this.throwError<any>('getPeopleViewModel', null, null, null)));
  }

  public uploadPersonAttachments(formData: FormData): Observable<ServerModels.ENESS_XREF_PeopleAttachmentDTO[]> {
    return this.http.post<ServerModels.ENESS_XREF_PeopleAttachmentDTO[]>(`${this.apiRoot}/api/Person/UploadPersonAttachments`, formData, this.httpFormData)
      .pipe(catchError(this.throwError<any>('getPeopleViewModel', 'defaultMsg', null, null)));
  }

  public uploadPersonAttachmentsStaging(formData: FormData): Observable<ServerModels.ENESS_XREF_PeopleAttachmentDTO[]> {
    return this.http.post<ServerModels.ENESS_XREF_PeopleAttachmentDTO[]>(`${this.apiRoot}/api/Person/UploadPersonAttachmentsStaging`, formData, this.httpFormData)
      .pipe(catchError(this.throwError<any>('uploadPersonAttachmentsStaging', 'defaultMsg', null, null)));
  }

  public getPersonsByStringOfIds(people: string): Observable<ServerModels.ENESS_Person[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personIds': people
      }
    };
    return this.http.get<ServerModels.ENESS_Person[]>(`${this.apiRoot}/api/Person/GetPersonsByStringOfIds`, options)
      .pipe(catchError(this.swallowError<any>('getPersonsByStringOfIds', 'defaultMsg', null, null))); 
  }

  public getPersonsByStringOfIdsForReturn(people: string): Observable<ServerModels.ENESS_PersonDTO[]> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'personIds': people
      }
    };
    return this.http.get<ServerModels.ENESS_PersonDTO[]>(`${this.apiRoot}/api/Person/GetPersonsByStringOfIdsForReturn`, options)
      .pipe(catchError(this.swallowError<any>('getPersonsByStringOfIds', 'defaultMsg', null, null))); 
  }

  public getAddressTypes() {
    this.http.get<ServerModels.ENESS_REF_AddressTypes[]>(`${this.apiRoot}/api/Person/GetAllAddressTypes`, this.httpOptions).pipe(
      take(1),
      catchError(this.swallowError<any>('getAddressTypes', 'defaultMsg', null, null))
    ).subscribe((results: ServerModels.ENESS_REF_AddressTypes[]) => this.addressTypesSubj.next(results));
  }

  public mergePeople(newPerson: ServerModels.PersonMergeViewModel): Observable<ServerModels.PersonMergeViewModel> {
    return this.http.post<ServerModels.PersonMergeViewModel>(`${this.apiRoot}/api/Person/MergePeople`, newPerson)
      .pipe(catchError(this.throwError<any>('mergePeople', null, null, null)));
  }

  public getCrimeGunId(crimeGunNumber: string): Observable<string> {
    let options = {
      headers: this.httpOptions.headers,
      params: {
        'crimeGunNumber': crimeGunNumber
      }
    };

    return this.http.get<string>(`${this.apiRoot}/api/Person/GetCrimeGunId`, options)
      .pipe(catchError(this.swallowError<any>('getCrimeGunId', 'defaultMsg', null, null))); 
  }

  public createEmptyPersonDTO(): ServerModels.ENESS_PersonDTO {
    const emptyPersonDto: ServerModels.ENESS_PersonDTO = <ServerModels.ENESS_PersonDTO>{
      CreatedOn: null,
      CreatedBy: null,
      PersonID: null,
      PersonStagingID: null,
      Deleted: false,
      LastName: null,
      FirstName: null,
      MiddleName: null,
      Suffix: null,
      Alias: null,
      NickName: null,
      GenderID: '',
      GenderName: null,
      RaceID: '',
      RaceName: null,
      RaceOther: null,
      IsGangMember: null,
      GangName: null,
      DateOfBirth: null,
      SSN: null,
      IsKnownFelon: null,
      PersonDescription: null,
      ENESS_XREF_PeopleAddresses: [],
      ENESS_XREF_PeoplePhoneNumbers: [],
      ENESS_XREF_PeopleEmailAccounts: [],
      ENESS_XREF_PeopleSocialMedia: [],
      ENESS_XREF_PeopleCityIDNumbers: [],
      ENESS_XREF_PeopleStateIDNumbers: [],
      ENESS_XREF_PeopleFBIFileNumbers: [],
      ENESS_XREF_PeopleDriversLicense: [],
      ENESS_XREF_PeopleAttachments: []
    };

    return emptyPersonDto;
  }
}
